import { SIGNUP_STEP_PARAM, SKIP_INTRO_PARAM } from './signupStepParam';
import { WALKTHROUGH_ONWARD_PARAM } from './signupWalkthrough';

/**
 * Param keys the funnel sets on ITSELF — transient social data from the
 * `connect-socials` step, and the `?step=` marker that gives an inline phase a
 * history entry. Not marketing/UTM data, so they're filtered out before
 * persisting to Firestore and `onboardingMarketingCapture` keeps only actual
 * attribution params.
 *
 * The filter below is a BLACKLIST, so anything the funnel adds to its own URL
 * and forgets to list here is persisted as though a campaign had sent it — and
 * forwarded to HubSpot on that assumption.
 */
export const kInternalFunnelParamKeys: ReadonlySet<string> = new Set([
  'instagram',
  'tiktok',
  'facebook',
  'youtube',
  'igFollowers',
  'tiktokFollowers',
  'ytFollowers',
  'fbFollowers',
  'maxFollowers',
  'linkInBio',
  'linkInBioSource',
  'businessType',
  'inferredCategoryLabel',
  'nicheSlug',
  'avatar',
  'bio',
  SIGNUP_STEP_PARAM,
  SKIP_INTRO_PARAM,
  WALKTHROUGH_ONWARD_PARAM,
]);

/**
 * The marketing/attribution params of a query string: everything that is not
 * funnel-internal, whatever its name — `utm_*`, ads click-ids (`fbclid`,
 * `gclid`, `ttclid`…), `r`, `sid`, `popstoreurl`, `onboarding_coupon`, and any
 * param a campaign invents next week without us shipping a change for it.
 *
 * FIRST value of each key wins, and the result is keyed, so a query that
 * repeats a param (`?utm_regintent=a&utm_regintent=b`) can never store two
 * values or a duplicate. First-value matches every other reader of these URLs:
 * `URLSearchParams.get()`, the server's param normalize, the `/signup` de-dupe
 * and the analytics session capture.
 */
export function marketingParamsFromSearch(
  search: string,
): Record<string, string> {
  const params = new URLSearchParams(search.replace(/^\?/, ''));
  const result: Record<string, string> = {};
  for (const key of new Set(params.keys())) {
    if (kInternalFunnelParamKeys.has(key)) continue;
    const value = params.get(key);
    if (typeof value === 'string') result[key] = value;
  }
  return result;
}

/**
 * `href` with every funnel-internal param taken off it, for the URLs recorded in
 * `onboardingMarketingCapture` (`url`, `entryUrl` → Salesforce `website` /
 * `source_url__c`).
 *
 * Same rule as {@link marketingParamsFromSearch}, so the recorded URL can never
 * disagree with the recorded params — and the creator's own name, handle, avatar
 * and email stop travelling to Salesforce a second time inside a query string.
 * Marketing params keep their original order; a repeated one collapses to its
 * first value.
 */
export function sanitizeMarketingUrl(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href; // not parseable — record it as-is rather than losing it
  }
  const search = new URLSearchParams(
    marketingParamsFromSearch(url.search),
  ).toString();
  return `${url.origin}${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
}
